import { useEffect, useMemo, useState } from 'react';
import {
  UMButton,
  UMFormStyles,
  UMIcon,
  UMInput,
  UMMessage,
  UMR,
  UMSectionTitle,
  UMSidePanel,
} from '../_components';
import { CRM_FIELD_IDS, FIELDS, fullLabel } from './catalog';
import { FieldControl } from './FieldControl';
import { chipText, effectiveCrmPresent, emptyValue, isActive, normalize } from './matching';
import type { FilterValue, FilterValues, ResetVariant, SectionDef } from './types';

export interface FiltersPanelProps {
  sections: SectionDef[];
  values: FilterValues;
  onChange: (values: FilterValues) => void;
  onClose: () => void;
  /** Variant B shows full production labels (no section context). */
  useFullLabels?: boolean;
  showChips: boolean;
  reset: ResetVariant;
}

const fieldDomId = (id: string) => `um-filter-${id}`;

function Counter({ n }: { n: number }) {
  if (!n) return null;
  return (
    <span
      aria-label={`${n} filtre${n > 1 ? 's' : ''} actif${n > 1 ? 's' : ''}`}
      style={{ minWidth: 20, height: 20, padding: '0 6px', borderRadius: 9999, background: UMR.blue, color: UMR.white, fontSize: 12, fontWeight: 700, lineHeight: '20px', textAlign: 'center' }}
    >
      {n}
    </span>
  );
}

function Chip({ text, onOpen, onRemove }: { text: string; onOpen: () => void; onRemove: () => void }) {
  const btn = { border: 'none', background: 'transparent', cursor: 'pointer', fontFamily: UMR.font, color: UMR.blueLink, padding: 0 } as const;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 2, maxWidth: '100%', borderRadius: 9999, background: UMR.badgeInfoBg, padding: '2px 4px 2px 10px' }}>
      <button type="button" onClick={onOpen} title={text} style={{ ...btn, fontSize: 12, lineHeight: '18px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textAlign: 'left' }}>
        {text}
      </button>
      <button type="button" onClick={onRemove} aria-label={`Retirer le filtre ${text}`} style={{ ...btn, display: 'flex' }}>
        <UMIcon name="close" family="icons" size={16} color={UMR.blueLink} />
      </button>
    </span>
  );
}

export function FiltersPanel({ sections, values, onChange, onClose, useFullLabels, showChips, reset }: FiltersPanelProps) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>(() => Object.fromEntries(sections.map((s) => [s.id, !!s.defaultOpen])));
  const [focusId, setFocusId] = useState<string | null>(null);

  useEffect(() => setOpen(Object.fromEntries(sections.map((s) => [s.id, !!s.defaultOpen]))), [sections]);

  useEffect(() => {
    if (!focusId) return;
    const el = document.getElementById(fieldDomId(focusId));
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el?.querySelector<HTMLElement>('input, select, [role="button"], button')?.focus({ preventScroll: true });
    setFocusId(null);
  }, [focusId]);

  const set = (id: string, v: FilterValue) => onChange({ ...values, [id]: v });
  const labelOf = (id: string) => (useFullLabels ? fullLabel(FIELDS[id]) : FIELDS[id].label);
  const crmPresent = effectiveCrmPresent(values);
  const crmDisabled = crmPresent === 'Non';

  const activeIds = useMemo(
    () => sections.flatMap((s) => s.groups.flatMap((g) => g.fields)).filter((id) => isActive(FIELDS[id], values[id])),
    [sections, values],
  );

  const q = normalize(search.trim());
  const visible = useMemo(() => {
    if (!q) return null;
    return sections
      .map((s) => {
        const sectionHit = normalize(s.title).includes(q);
        const groups = s.groups
          .map((g) => ({
            ...g,
            fields: sectionHit || (g.title && normalize(g.title).includes(q))
              ? g.fields
              : g.fields.filter((id) => normalize(labelOf(id)).includes(q) || normalize(fullLabel(FIELDS[id])).includes(q)),
          }))
          .filter((g) => g.fields.length);
        return { ...s, groups };
      })
      .filter((s) => s.groups.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, sections, useFullLabels]);
  const shown = visible ?? sections;
  const matchCount = visible?.reduce((n, s) => n + s.groups.reduce((m, g) => m + g.fields.length, 0), 0) ?? 0;

  const goTo = (id: string) => {
    const section = sections.find((s) => s.groups.some((g) => g.fields.includes(id)));
    setSearch('');
    if (section) setOpen((o) => ({ ...o, [section.id]: true }));
    setFocusId(id);
  };

  const renderField = (id: string) => {
    const f = FIELDS[id];
    const disabled = crmDisabled && CRM_FIELD_IDS.includes(id);
    return (
      <div
        key={id}
        id={fieldDomId(id)}
        aria-disabled={disabled || undefined}
        aria-describedby={disabled ? 'um-crm-disabled' : undefined}
        style={disabled ? { opacity: 0.45, pointerEvents: 'none' } : undefined}
      >
        <fieldset disabled={disabled} style={{ border: 'none', margin: 0, padding: 0, minWidth: 0 }}>
          <FieldControl
            field={f}
            label={labelOf(id)}
            value={values[id]}
            onChange={(v) => set(id, v)}
            search={search}
            displayValue={id === 'crmPresent' ? crmPresent : undefined}
          />
        </fieldset>
        {id === 'crmPresent' && crmDisabled && (
          <p id="um-crm-disabled" style={{ margin: '4px 0 0', fontSize: 12, lineHeight: '16px', color: UMR.textSecondary }}>
            Les critères d'offre ne s'appliquent qu'aux bâtiments présents dans une offre.
          </p>
        )}
      </div>
    );
  };

  const clearAll = () => onChange({});
  const n = activeIds.length;
  const liveHint = (text: string, icon = true) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, color: UMR.textSecondary, whiteSpace: 'nowrap' }}>
      {icon && <UMIcon name="bolt" family="icons" size={14} color={UMR.textSecondary} />}
      {text}
    </span>
  );

  const footer =
    reset === 'prod' ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', width: '100%', gap: 8 }}>
        <UMButton disabled={!n} onClick={clearAll}>Réinitialiser</UMButton>
      </div>
    ) : reset === 'secondaire' ? (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', gap: 8 }}>
        <UMButton variant="secondary" icon="filter_alt_off" disabled={!n} onClick={clearAll}>
          Effacer les filtres{n ? ` (${n})` : ''}
        </UMButton>
        {liveHint('Appliqués en direct', false)}
      </div>
    ) : (
      <div style={{ display: 'flex', alignItems: 'center', width: '100%', gap: 8 }}>
        {liveHint('Appliqués directement sur la carte')}
      </div>
    );

  const clearLink = (
    <button
      type="button"
      onClick={clearAll}
      className="um-hover-grey"
      style={{ display: 'inline-flex', alignItems: 'center', gap: 4, border: 'none', background: 'transparent', padding: '2px 4px', cursor: 'pointer', fontFamily: UMR.font, fontSize: 12, color: UMR.blueLink }}
    >
      <UMIcon name="filter_alt_off" family="icons" size={14} color={UMR.blueLink} />
      Tout effacer
    </button>
  );
  const activeHeader = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: UMR.textLabel }}>
        {n} filtre{n > 1 ? 's' : ''} actif{n > 1 ? 's' : ''}
      </div>
      {reset === 'lien' && clearLink}
    </div>
  );

  return (
    <UMSidePanel title="Filtres des bâtiments" onClose={onClose} footer={footer}>
      <UMFormStyles />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <UMInput value={search} onChange={setSearch} placeholder="Rechercher un filtre" ariaLabel="Rechercher un filtre" icon="search" />

        {!showChips && reset === 'lien' && n > 0 && !q && activeHeader}
        {showChips && n > 0 && !q && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {activeHeader}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              {activeIds.map((id) => (
                <Chip key={id} text={chipText(FIELDS[id], values[id], values)} onOpen={() => goTo(id)} onRemove={() => set(id, emptyValue(FIELDS[id]))} />
              ))}
            </div>
          </div>
        )}

        {q && (
          <div aria-live="polite" style={{ fontSize: 12, color: UMR.textSecondary }}>
            {matchCount ? `${matchCount} filtre${matchCount > 1 ? 's' : ''} trouvé${matchCount > 1 ? 's' : ''}` : ''}
          </div>
        )}
        {q && !matchCount && (
          <UMMessage>
            Aucun filtre ne correspond à « {search.trim()} ».{' '}
            <button type="button" onClick={() => setSearch('')} style={{ border: 'none', background: 'none', padding: 0, color: UMR.blueLink, textDecoration: 'underline', cursor: 'pointer', fontFamily: UMR.font, fontSize: 14 }}>
              Effacer
            </button>
          </UMMessage>
        )}

        {shown.map((s) => {
          const isOpen = q ? true : !!open[s.id];
          const count = s.groups.flatMap((g) => g.fields).filter((id) => activeIds.includes(id)).length;
          return (
            <div key={s.id} style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => !q && setOpen((o) => ({ ...o, [s.id]: !o[s.id] }))}
                style={{ display: 'flex', alignItems: 'center', gap: 8, border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: UMR.font, fontSize: 14, lineHeight: '21px', color: UMR.black, textAlign: 'left' }}
              >
                <span style={{ fontWeight: 600, flex: 1 }}>{s.title}</span>
                <Counter n={count} />
                <UMIcon name={isOpen ? 'arrow_drop_down' : 'arrow_right'} family="icons" size={24} color={UMR.textSecondary} />
              </button>
              {isOpen && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {s.groups.map((g, gi) => (
                    <div key={g.title ?? gi} style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: g.title && gi > 0 ? 8 : 0 }}>
                      {g.title && (
                        <UMSectionTitle>
                          <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: 0.4, textTransform: 'uppercase', color: UMR.textLabel }}>{g.title}</span>
                        </UMSectionTitle>
                      )}
                      {g.fields.map(renderField)}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </UMSidePanel>
  );
}
